import React from 'react';
import {
  Zap,
  Check,
  Shield,
  TrendingUp,
  BarChart,
  Crown,
  X,
} from 'lucide-react';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  price?: string;
}

export default function UpgradeModal({
  isOpen,
  onClose,
  onConfirm,
  loading,
  price,
}: UpgradeModalProps) {
  const { t } = useTranslation();
  const { createRipple } = useRipple();

  if (!isOpen) return null;

  const benefits = [
    { icon: TrendingUp, text: t('upgradeModal.unlimitedGames') },
    { icon: BarChart, text: t('upgradeModal.unlimitedAnalyses') },
    { icon: Crown, text: t('upgradeModal.prioritySupport') },
  ];

  return (
    <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50'>
      <div className='glass-modal max-w-lg w-full p-0 overflow-hidden'>
        {/* Header */}
        <div className='relative bg-gradient-to-br from-primary-600 to-primary-800 px-6 pt-8 pb-16 text-center'>
          <button
            onClick={(e) => {
              createRipple(e);
              onClose();
            }}
            className='absolute top-4 right-4 text-white/80 hover:text-white transition-colors btn-glass p-1.5 rounded-lg'
          >
            <X className='h-5 w-5' />
          </button>

          <div className='flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mx-auto mb-4'>
            <Crown className='h-8 w-8 text-yellow-300' />
          </div>

          <h2 className='text-2xl font-bold text-white mb-2'>
            {t('upgradeModal.title')}
          </h2>
          <p className='text-primary-200 text-sm'>
            {t('upgradeModal.subtitle')}
          </p>
        </div>

        {/* Price section */}
        <div className='-mt-10 px-6'>
          <div className='bg-white rounded-2xl shadow-lg border border-slate-100 px-6 py-5 text-center'>
            <div className='text-3xl font-bold text-heading'>
              {price || `CHF 37`}<span className='text-lg text-muted font-normal'>/mois</span>
            </div>
            <p className='text-xs text-muted mt-1'>
              {t('upgradeModal.annualNotice')}
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className='px-6 pt-6 pb-4 space-y-3'>
          <p className='text-sm font-semibold text-heading'>
            {t('upgradeModal.whatYouGet')}
          </p>
          {benefits.map((benefit) => (
            <div key={benefit.text} className='flex items-start gap-3'>
              <div className='flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5'>
                <Check className='h-3.5 w-3.5 text-emerald-600' />
              </div>
              <p className='text-sm text-body'>{benefit.text}</p>
            </div>
          ))}
        </div>

        {/* Guarantee banner */}
        <div className='mx-6 mb-4 bg-amber-50 border border-amber-200/50 rounded-xl px-4 py-3 flex items-start gap-3'>
          <Shield className='h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5' />
          <div>
            <p className='text-sm font-semibold text-amber-800'>
              {t('upgradeModal.guaranteeTitle')}
            </p>
            <p className='text-xs text-amber-700'>
              {t('upgradeModal.guaranteeText')}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className='px-6 pb-6'>
          <button
            onClick={(e) => {
              createRipple(e);
              onConfirm();
            }}
            disabled={loading}
            className='w-full btn-mui-primary btn-ripple py-3 text-base font-semibold'
          >
            {loading ? (
              <span className='flex items-center justify-center gap-2'>
                <svg className='animate-spin h-5 w-5' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                </svg>
                {t('common.loading')}
              </span>
            ) : (
              <span className='flex items-center justify-center gap-2'>
                <Zap className='h-5 w-5' />
                {t('upgradeModal.confirmCta')}
              </span>
            )}
          </button>
          <p className='text-center text-xs text-muted mt-3'>
            {t('upgradeModal.cancelAnywhere')}
          </p>
        </div>
      </div>
    </div>
  );
}
